import React, { useEffect, useState } from "react";
import api from "../../api/api";
import styles from "../../styles/WalletPassbook.module.css";

// Helper for formatting date
function formatDate(dateStr) {
  const d = new Date(dateStr);
  return {
    date: d.toLocaleDateString('en-IN'),
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  };
}

// Helper for formatting currency
function formatCurrency(amount) {
  const formattedAmount = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
  return `₹${formattedAmount}`;
}

// Helper for formatting transaction reference
function formatTransactionRef(txnRef, reason) {
  // Check if it's an order payment transaction
  if (reason && reason.toLowerCase().includes('order') && reason.toLowerCase().includes('payment')) {
    // Extract order ID from the transaction reference or reason
    const orderMatch = txnRef.match(/\d+/) || reason.match(/\d+/);
    if (orderMatch) {
      const orderId = orderMatch[0];
      const formattedOrderId = String(orderId).padStart(6, "0");
      return `Order #${formattedOrderId}`;
    }
  }
  return txnRef;
}

const PAGE_SIZE = 10;

const WalletPassbook = () => {
  const userid = JSON.parse(localStorage.getItem("user"))?.userid;
  const [passbook, setPassbook] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, credit, debit

  // Filter passbook based on search and filter type
  const filteredPassbook = passbook.filter(txn => {
    const formattedRef = formatTransactionRef(txn.txn_ref, txn.reason);
    const matchesSearch = txn.reason.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         txn.txn_ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         formattedRef.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === "all" || 
                         (filterType === "credit" && (parseFloat(txn.credit) || 0) > 0) ||
                         (filterType === "debit" && (parseFloat(txn.debit) || 0) > 0);
    
    return matchesSearch && matchesFilter;
  });

  // Calculate total pages based on filtered passbook length
  const totalPages = Math.max(1, Math.ceil(filteredPassbook.length / PAGE_SIZE));

  useEffect(() => {
    const fetchPassbook = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/wallet/wallet-passbook/${userid}`);
        if (res.data.status === "success") {
          setPassbook(res.data.passbook || []);
        } else {
          setPassbook([]);
        }
      } catch (err) {
        console.error("Error fetching passbook:", err);
        setPassbook([]);
      }
      setLoading(false);
    };
    fetchPassbook();
  }, [userid]);

  // Get only the records for the current page
  const paginatedPassbook = filteredPassbook.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  // Reset to first page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterType]);

  // Responsive detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={styles.modernWalletPassbook}>
      {/* Header Section */}
      <div className={styles.passbookHeader}>
        <div className={styles.headerContent}>
          <h1 className={styles.passbookTitle}>
            <span className={styles.titleIcon}>💰</span>
            Wallet Passbook
          </h1>
          <p className={styles.passbookSubtitle}>
            Track your wallet transactions and balance history
          </p>
        </div>
      </div>

      {/* Filter Controls */}
      <div className={styles.passbookControls}>
        <div className={styles.searchContainer}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterContainer}>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="all">All Transactions</option>
            <option value="credit">Credit Only</option>
            <option value="debit">Debit Only</option>
          </select>
        </div>
      </div>

      {/* Summary Cards */}
      {passbook.length > 0 && (
        <div className={styles.summaryCards}>
          <div className={`${styles.summaryCard} ${styles.creditCard}`}>
            <div className={styles.cardIcon}>💚</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Credits</div>
              <div className={styles.cardValue}>
                {formatCurrency(passbook.reduce((sum, txn) => sum + (parseFloat(txn.credit) || 0), 0))}
              </div>
            </div>
          </div>
          
          <div className={`${styles.summaryCard} ${styles.debitCard}`}>
            <div className={styles.cardIcon}>❤️</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Total Debits</div>
              <div className={styles.cardValue}>
                {formatCurrency(passbook.reduce((sum, txn) => sum + (parseFloat(txn.debit) || 0), 0))}
              </div>
            </div>
          </div>
          
          <div className={`${styles.summaryCard} ${styles.balanceCard}`}>
            <div className={styles.cardIcon}>💎</div>
            <div className={styles.cardContent}>
              <div className={styles.cardLabel}>Current Balance</div>
              <div className={styles.cardValue}>
                {formatCurrency(
                  passbook.reduce((sum, txn) => sum + (parseFloat(txn.credit) || 0), 0) -
                  passbook.reduce((sum, txn) => sum + (parseFloat(txn.debit) || 0), 0)
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className={styles.passbookContent}>
        {loading ? (
          <div className={styles.loadingContainer}>
            <div className={styles.loadingSpinner}></div>
            <div className={styles.loadingText}>Loading your transactions...</div>
          </div>
        ) : filteredPassbook.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📋</div>
            <h3 className={styles.emptyTitle}>
              {passbook.length === 0 ? "No Transactions Found" : "No Matching Transactions"}
            </h3>
            <p className={styles.emptyDescription}>
              {passbook.length === 0 
                ? "Your wallet passbook is empty. Start making transactions to see them here."
                : "Try adjusting your search or filter criteria."
              }
            </p>
          </div>
        ) : isMobile ? (
          // Mobile: Enhanced Card view
          <div className={styles.mobileTransactions}>
            {paginatedPassbook.map((txn, idx) => {
              const dateTime = formatDate(txn.transaction_date);
              const isCredit = (parseFloat(txn.credit) || 0) > 0;
              
              return (
                <div key={txn.txn_ref || idx} className={`${styles.transactionCard} ${isCredit ? styles.credit : styles.debit}`}>
                  <div className={styles.transactionHeader}>
                    <div className={styles.transactionType}>
                      <span className={`${styles.typeIcon} ${isCredit ? styles.creditIcon : styles.debitIcon}`}>
                        {isCredit ? '↗️' : '↘️'}
                      </span>
                      <span className={styles.typeLabel}>
                        {isCredit ? 'Credit' : 'Debit'}
                      </span>
                    </div>
                    <div className={styles.transactionAmount}>
                      {formatCurrency(isCredit ? (parseFloat(txn.credit) || 0) : (parseFloat(txn.debit) || 0))}
                    </div>
                  </div>
                  
                  <div className={styles.transactionDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>📅 Date:</span>
                      <span className={styles.detailValue}>{dateTime.date}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>⏰ Time:</span>
                      <span className={styles.detailValue}>{dateTime.time}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>🔖 Reference:</span>
                      <span className={`${styles.detailValue} ${styles.transactionRef}`}>{formatTransactionRef(txn.txn_ref, txn.reason)}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>📝 Reason:</span>
                      <span className={styles.detailValue}>{txn.reason}</span>
                    </div>
                    <div className={`${styles.detailRow} ${styles.balanceRow}`}>
                      <span className={styles.detailLabel}>💰 Balance:</span>
                      <span className={`${styles.detailValue} ${styles.balanceAmount}`}>
                        {formatCurrency(txn.current_balance)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Desktop: Enhanced Table view
          <div className={styles.desktopTransactions}>
            <div className={styles.tableContainer}>
              <table className={styles.transactionsTable}>
                <thead>
                  <tr>
                    <th>Date & Time</th>
                    <th>Transaction Ref</th>
                    <th>Description</th>
                    <th>Credit</th>
                    <th>Debit</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPassbook.map((txn, idx) => {
                    const dateTime = formatDate(txn.transaction_date);
                    const isCredit = (parseFloat(txn.credit) || 0) > 0;
                    
                    return (
                      <tr key={txn.txn_ref || idx} className={`${styles.transactionRow} ${isCredit ? styles.creditRow : styles.debitRow}`}>
                        <td className={styles.dateCell}>
                          <div className={styles.dateContainer}>
                            <div className={styles.date}>{dateTime.date}</div>
                            <div className={styles.time}>{dateTime.time}</div>
                          </div>
                        </td>
                        <td className={styles.refCell}>
                          <span className={styles.transactionRef}>{formatTransactionRef(txn.txn_ref, txn.reason)}</span>
                        </td>
                        <td className={styles.descriptionCell}>
                          <span className={styles.reason}>{txn.reason}</span>
                        </td>
                        <td className={styles.creditCell}>
                          {(parseFloat(txn.credit) || 0) > 0 ? (
                            <span className={styles.creditAmount}>
                              <span className={styles.amountIcon}>↗️</span>
                              {formatCurrency(parseFloat(txn.credit) || 0)}
                            </span>
                          ) : (
                            <span className={styles.noAmount}>-</span>
                          )}
                        </td>
                        <td className={styles.debitCell}>
                          {(parseFloat(txn.debit) || 0) > 0 ? (
                            <span className={styles.debitAmount}>
                              <span className={styles.amountIcon}>↘️</span>
                              {formatCurrency(parseFloat(txn.debit) || 0)}
                            </span>
                          ) : (
                            <span className={styles.noAmount}>-</span>
                          )}
                        </td>
                        <td className={styles.balanceCell}>
                          <span className={styles.balanceAmount}>
                            {formatCurrency(txn.current_balance)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {filteredPassbook.length > PAGE_SIZE && (
          <div className={styles.paginationContainer}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`${styles.paginationBtn} ${styles.prevBtn}`}
            >
              <span className={styles.btnIcon}>⬅️</span>
              Previous
            </button>
            
            <div className={styles.paginationInfo}>
              <span className={styles.pageInfo}>
                Page <span className={styles.currentPage}>{page}</span> of <span className={styles.totalPages}>{totalPages}</span>
              </span>
              <span className={styles.resultsInfo}>
                Showing {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, filteredPassbook.length)} of {filteredPassbook.length} transactions
              </span>
            </div>
            
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`${styles.paginationBtn} ${styles.nextBtn}`}
            >
              Next
              <span className={styles.btnIcon}>➡️</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletPassbook;